import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useFrontDesk, FRONT_DESK_ALLOWED_ROUTES } from "@/contexts/FrontDeskContext";
import { useCapabilities } from "@/hooks/useCapabilities";
import type { Capability } from "@/lib/permissions";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Legacy role-array gate. Still the primary mechanism for most
   * routes (and the only one that handles Solo Artists correctly
   * today). New routes can use `requireCapability` for centralized
   * gating; existing routes stay on `allowedRoles` until Solo Artist
   * is modeled as a first-class pseudo-role in the capability set.
   */
  allowedRoles?: Array<"owner" | "manager" | "artist" | "apprentice" | "piercer">;
  /**
   * Capability-based gate (preferred for new routes). If provided,
   * the route is accessible only to users whose effective capability
   * set includes the listed capability. All overlays — Solo Artist,
   * front-desk, platform admin, apprentice canTattoo, manager
   * is_also_artist, Solo Free locks — are handled inside
   * `computeCapabilities`, so this single check covers every case.
   */
  requireCapability?: Capability;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  requireCapability,
}: ProtectedRouteProps) {
  const { user, loading, currentRole, memberships } = useAuth();
  const { can } = useCapabilities();
  const location = useLocation();
  const { isFrontDeskMode } = useFrontDesk();
  const { t } = useTranslation("system");

  // Front desk mode: block restricted routes
  if (isFrontDeskMode && !FRONT_DESK_ALLOWED_ROUTES.some((r) => location.pathname.startsWith(r))) {
    return <Navigate to="/appointments" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t("common.loading", { ns: "common" })}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Solo artists (no memberships) get owner-like access for personal features
  const isSoloArtist = memberships.length === 0;

  // Capability check — defense in depth against direct-URL access. The
  // capability model accounts for every overlay (Solo Artist, front
  // desk, platform admin, apprentice canTattoo, Solo Free locks), so
  // this single check covers every case.
  if (requireCapability && !can(requireCapability)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <h1 className="font-display text-4xl text-foreground mb-4">{t("protectedRoute.accessDenied")}</h1>
          <p className="text-muted-foreground">
            {t("protectedRoute.noPermission")}
          </p>
        </div>
      </div>
    );
  }

  // Check role-based access if roles are specified
  if (allowedRoles && !isSoloArtist) {
    // For shop members, check their role
    if (currentRole && !allowedRoles.includes(currentRole)) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-4xl text-foreground mb-4">{t("protectedRoute.accessDenied")}</h1>
            <p className="text-muted-foreground">
              {t("protectedRoute.noPermission")}
            </p>
          </div>
        </div>
      );
    }
  }

  // Solo artists with allowedRoles that don't include owner/manager are blocked
  // from shop-only features (Team, Apprenticeships, etc.)
  if (isSoloArtist && allowedRoles && !allowedRoles.includes("owner") && !allowedRoles.includes("manager")) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <h1 className="font-display text-4xl text-foreground mb-4">{t("protectedRoute.featureNotAvailable")}</h1>
          <p className="text-muted-foreground">
            {t("protectedRoute.studioOnly")}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
